import React, { useState } from "react";
import { ExternalLink, FileText, HelpCircle } from "lucide-react";

interface CitationMatch {
  fullMatch: string;
  citationKey: string;
  description: string;
  startIndex: number;
  endIndex: number;
}

interface AdditionalMaterial {
  ek_id: number;
  icerik_json: {
    title: string;
    description?: string | null;
    hooks?: Array<{
      title: string;
      description: string;
      expected_outcome: string;
      materials_needed: string;
    }>;
    misconceptions?: Array<{
      misconception: string;
      clarification: string;
      why_misconception_occurs: string;
      fix_strategies: string;
      real_classroom_example: string;
    }>;
  };
  toolType: string;
}

interface CitationParserProps {
  content: string;
  additionalMaterials: AdditionalMaterial[];
  onCitationClick?: (material: AdditionalMaterial, description: string) => void;
}

// Parse citations in format [EK-1](description) or [EK-2](description)
const parseCitations = (content: string): CitationMatch[] => {
  const citationRegex = /\[EK-(\d+)\]\(([^)]+)\)/g;
  const matches: CitationMatch[] = [];
  let match;

  while ((match = citationRegex.exec(content)) !== null) {
    matches.push({
      fullMatch: match[0],
      citationKey: `EK-${match[1]}`,
      description: match[2],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }

  return matches.sort((a, b) => a.startIndex - b.startIndex);
};

// Simple Popover component
const Popover: React.FC<{
  children: React.ReactNode;
  content: string;
  isVisible: boolean;
}> = ({ children, content, isVisible }) => {
  return (
    <div className="relative inline-block">
      {children}
      {isVisible && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg whitespace-nowrap z-10">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
        </div>
      )}
    </div>
  );
};

const CitationParser: React.FC<CitationParserProps> = ({
  content,
  additionalMaterials,
  onCitationClick,
}) => {
  const [hoveredCitation, setHoveredCitation] = useState<string | null>(null);
  const citations = parseCitations(content);

  if (citations.length === 0) {
    return <span>{content}</span>;
  }

  const renderContent = () => {
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;

    citations.forEach((citation, index) => {
      // Add text before citation
      if (citation.startIndex > lastIndex) {
        elements.push(
          <span key={`text-${index}`}>
            {content.substring(lastIndex, citation.startIndex)}
          </span>,
        );
      }

      // Find matching additional material
      const ekNumber = parseInt(citation.citationKey.replace("EK-", ""));
      const material = additionalMaterials.find((m) => m.ek_id === ekNumber);
      const citationId = `citation-${index}`;

      // Render citation as clickable link with description text
      elements.push(
        <Popover
          key={citationId}
          content={citation.citationKey}
          isVisible={hoveredCitation === citationId}
        >
          <button
            onClick={() =>
              material && onCitationClick?.(material, citation.description)
            }
            onMouseEnter={() => setHoveredCitation(citationId)}
            onMouseLeave={() => setHoveredCitation(null)}
            className="inline-flex items-center gap-1 px-2 py-1 mx-1 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors duration-200 border border-blue-200 hover:border-blue-300"
            title={
              material
                ? `Click to view ${citation.citationKey}`
                : "Additional material not found"
            }
          >
            {material?.toolType === "LESSON_HOOK" ? (
              <FileText className="w-3 h-3" />
            ) : material?.toolType === "COMMON_MISCONCEPTION" ? (
              <HelpCircle className="w-3 h-3" />
            ) : (
              <ExternalLink className="w-3 h-3" />
            )}
            <span>{citation.description}</span>
          </button>
        </Popover>,
      );

      lastIndex = citation.endIndex;
    });

    // Add remaining text
    if (lastIndex < content.length) {
      elements.push(<span key="text-end">{content.substring(lastIndex)}</span>);
    }

    return elements;
  };

  return <span className="leading-relaxed">{renderContent()}</span>;
};

export default CitationParser;
